import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Tooltip,
  Fab,
  Chip
} from '@mui/material';
import { Checkbox, FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DoneAllIcon from '@mui/icons-material/DoneAll';
// Category icons
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WineBarIcon from '@mui/icons-material/WineBar';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SignpostIcon from '@mui/icons-material/Signpost';
import CampaignIcon from '@mui/icons-material/Campaign';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DescriptionIcon from '@mui/icons-material/Description';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SocialDistanceIcon from '@mui/icons-material/SocialDistance';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import FolderIcon from '@mui/icons-material/Folder';
import LandscapeIcon from '@mui/icons-material/Landscape';
import CelebrationIcon from '@mui/icons-material/Celebration';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import AppsIcon from '@mui/icons-material/Apps';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MovieIcon from '@mui/icons-material/Movie';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { ICategory, ITemplate } from '../types/damTypes';
import TemplateFieldsForm from './TemplateFieldsForm';
import { v4 as uuidv4 } from 'uuid';
import { useUploadManager } from '../contexts/UploadManagerContext';
import axiosInstance from '../services/axiosInstance';
import { getTemplateUrlValidationErrors } from '../utils/templateFieldValidation';
import {
  buildUploadTooLargeMessage,
  formatUploadBytes,
  formatUploadEta,
  formatUploadSpeed,
  getUploadErrorMessage,
  splitFilesByUploadSize,
} from '../utils/uploadUi';

interface EnhancedUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  categories: ICategory[];
  destination?: 'library' | 'archive';
}

// Steps for the upload wizard (Swedish)
const steps = ['Välj filer', 'Välj mall', 'Lägg till metadata', 'Ladda upp'];

// Temporary definition for IUploadFile
type IUploadFile = any; // Replace with actual definition later

type UploadPhase = 'idle' | 'uploading' | 'processing' | 'completed' | 'error';

type UploadStats = {
  uploadedBytes: number;
  totalBytes: number;
  speedBps: number;
  etaSeconds?: number;
  currentFileName?: string;
  currentFileIndex: number;
  currentFileProgress: number;
  phase: UploadPhase;
};

const createInitialUploadStats = (): UploadStats => ({
  uploadedBytes: 0,
  totalBytes: 0,
  speedBps: 0,
  etaSeconds: undefined,
  currentFileName: undefined,
  currentFileIndex: 0,
  currentFileProgress: 0,
  phase: 'idle',
});

// Function to get appropriate icon for category
const getCategoryIcon = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  
  if (name.includes('produkt') || name.includes('grocery')) return ShoppingCartIcon;
  if (name.includes('vin') || name.includes('wine') || name.includes('alkohol')) return WineBarIcon;
  if (name.includes('butik') || name.includes('store') || name.includes('handel')) return StorefrontIcon;
  if (name.includes('miljö') || name.includes('miljo') || name.includes('environment')) return LandscapeIcon;
  if (name.includes('event')) return CelebrationIcon;
  if (name.includes('logo') || name.includes('brand')) return BrandingWatermarkIcon;
  if (name.includes('ikon') || name.includes('icon')) return AppsIcon;
  if (name.includes('kod') || name.includes('ean') || name.includes('barcode') || name.includes('qr')) return QrCode2Icon;
  if (name.includes('film') || name.includes('video')) return MovieIcon;
  if (name.includes('recept') || name.includes('recipe')) return RestaurantMenuIcon;
  if (name.includes('blandat') || name.includes('mixed')) return WidgetsIcon;
  if (name.includes('skylt') || name.includes('sign') || name.includes('märkning')) return SignpostIcon;
  if (name.includes('kampanj') || name.includes('campaign') || name.includes('reklam')) return CampaignIcon;
  if (name.includes('meny') || name.includes('menu') || name.includes('matsedel')) return MenuBookIcon;
  if (name.includes('broschyr') || name.includes('dokument') || name.includes('material')) return DescriptionIcon;
  if (name.includes('foto') || name.includes('bild') || name.includes('image') || name.includes('media')) return PhotoCameraIcon;
  if (name.includes('social') || name.includes('some') || name.includes('facebook') || name.includes('instagram')) return SocialDistanceIcon;
  if (name.includes('mat') || name.includes('food') || name.includes('restaurang')) return RestaurantIcon;
  if (name.includes('livsmedel') || name.includes('dagligvara')) return LocalGroceryStoreIcon;
  
  // Default fallback icon
  return FolderIcon;
};

interface TagsChipsInputProps {
  value: string;
  onChange: (val: string) => void;
  size?: 'small' | 'medium';
  label?: string;
  helperText?: string;
}

// Keep the tags input at module scope so the TextField is not remounted
// every time the parent modal state updates.
const TagsChipsInput: React.FC<TagsChipsInputProps> = ({
  value,
  onChange,
  size = 'medium',
  label = 'Taggar (kommaseparerade)',
  helperText,
}) => {
  const tokens = (value || '')
    .split(',')
    .map(t => t.trim())
    .filter(t => t.length > 0);
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const hasContent = tokens.length > 0 || input.length > 0;

  const commit = (nextTokens: string[]) => onChange(nextTokens.join(', '));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const trimmed = input.trim();
      if (trimmed && !tokens.includes(trimmed)) {
        commit([...tokens, trimmed]);
      }
      setInput('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const trimmed = input.trim();
    if (trimmed && !tokens.includes(trimmed)) {
      commit([...tokens, trimmed]);
      setInput('');
    }
  };

  return (
    <TextField
      fullWidth
      size={size}
      label={label}
      value={input}
      onChange={(e) => setInput(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      helperText={helperText}
      InputLabelProps={{ shrink: hasContent || isFocused }}
      InputProps={{
        startAdornment: tokens.length > 0
          ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                {tokens.map((t, idx) => (
                  <Chip
                    key={`${t}-${idx}`}
                    label={t}
                    onDelete={() => commit(tokens.filter(x => x !== t))}
                    size={size === 'small' ? 'small' : undefined}
                  />
                ))}
              </Box>
            )
          : undefined,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: size === 'small' ? 0.5 : 0.75,
        },
        '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          maxWidth: '100%',
          marginRight: 0,
        },
        '& .MuiOutlinedInput-input': {
          flexGrow: 1,
          minWidth: size === 'small' ? 120 : 160,
          width: 'auto',
          p: 0,
          py: size === 'small' ? '6px' : '8px',
          caretColor: 'currentColor',
        },
        '& .MuiChip-root': {
          my: 0.25,
        },
      }}
    />
  );
};

const EnhancedUploadModal: React.FC<EnhancedUploadModalProps> = ({
  open,
  onClose,
  onUploadSuccess,
  categories,
  destination = 'library',
}) => {
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [files, setFiles] = useState<IUploadFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState<UploadStats>(createInitialUploadStats);
  
  // Template-related state
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate | null>(null);
  const [selectedMainCategoryId, setSelectedMainCategoryId] = useState<string>('');
  
  // Metadata state
  const [commonMetadata, setCommonMetadata] = useState<Record<string, any>>({});
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [applyToAllFiles, setApplyToAllFiles] = useState(true);
  const [selectedSubcategoryIds, setSelectedSubcategoryIds] = useState<string[]>([]);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { enqueueBackgroundUpload } = useUploadManager();

  // Helper: extract 5 digits with optional trailing F from filename start
  const extractArticleNumber = (fileName: string): string | null => {
    // Use just the base name in case path-like names slip through
    const base = fileName.split(/(\\|\/)/).pop() || fileName;
    const match = base.match(/^(\d{5})([fF])?(?=[^0-9]|$)/);
    if (!match) return null;
    const digits = match[1];
    const suffix = (match[2] || '').toUpperCase();
    return `${digits}${suffix}`;
  };

  // Helper: find the Artikelnummer field in the selected template (by id/name/label)
  const getArticleNumberField = () => {
    if (!selectedTemplate) return null;
    const fields = selectedTemplate.fields || [];
    // Prioritize id matches, then label/name includes
    const byId =
      fields.find(f => (f.id || '').toLowerCase() === 'artikelnummer') ||
      fields.find(f => (f.id || '').toLowerCase() === 'artnr') ||
      fields.find(f => (f.id || '').toLowerCase() === 'artnummer') ||
      fields.find(f => (f.id || '').toLowerCase() === 'articlenumber');

    if (byId) return byId;

    const byLabelOrName = fields.find(f => {
      const lbl = (f.label || '').toLowerCase();
      const name = (f.name || '').toLowerCase();
      return (
        lbl.includes('artikelnummer') ||
        name.includes('artikelnummer') ||
        lbl.includes('artnr') ||
        name.includes('artnr')
      );
    });
    return byLabelOrName || null;
  };

  // Helper: find VIN Produktnamn field (System ID: Produktnamn_vin_id)
  const getWineProductNameField = () => {
    if (!selectedTemplate) return null;
    const fields = selectedTemplate.fields || [];
    // Prefer exact system-id match by name (template fieldName)
    const byName =
      fields.find((f: any) => (f.name || '') === 'Produktnamn_vin_id') ||
      fields.find((f: any) => String(f.name || '').toLowerCase() === 'produktnamn_vin_id');
    if (byName) return byName;
    // Fallback: some legacy templates may have id/name swapped
    const byId =
      fields.find((f: any) => (f.id || '') === 'Produktnamn_vin_id') ||
      fields.find((f: any) => String(f.id || '').toLowerCase() === 'produktnamn_vin_id');
    return byId || null;
  };

  // Fetch templates on mount
  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);
  
  // When a template is selected, set it as the current template
  useEffect(() => {
    console.log('Template selection effect running, selectedTemplateId:', selectedTemplateId);
    if (selectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t._id === selectedTemplateId);
      console.log('Selected template:', template);
      
      if (template) {
        console.log('Template fields:', template.fields);
        console.log('Template field count:', template.fields?.length || 0);
        setSelectedTemplate(template);
        
        // Initialize common metadata with default values from template
        const initialMetadata: Record<string, any> = {};
        if (template.fields && template.fields.length > 0) {
          template.fields.forEach(field => {
            if (field.defaultValue !== undefined) {
              initialMetadata[field.id] = field.defaultValue;
            }
          });
          console.log('Initializing metadata with values:', initialMetadata);
        } else {
          console.log('No fields found in template to initialize metadata');
        }
        
        setCommonMetadata(initialMetadata);
        
        // Also initialize metadata for each file
        setFiles(prevFiles => prevFiles.map(file => ({
          ...file,
          metadata: { ...initialMetadata }
        })));
      }
    } else {
      setSelectedTemplate(null);
    }
  }, [selectedTemplateId, templates]);
  
  // When main category changes, find templates for that category
  useEffect(() => {
    if (selectedMainCategoryId && templates.length > 0) {
      console.log('Selected main category:', selectedMainCategoryId);
      console.log('Available templates:', templates);
      
      const matchingTemplates = templates.filter(template => 
        template.categoryIds.includes(selectedMainCategoryId)
      );
      
      console.log('Matching templates for category:', matchingTemplates);
      
      if (matchingTemplates.length > 0) {
        // Find default template or use first one
        const defaultTemplate = matchingTemplates.find(t => t.isDefault) || matchingTemplates[0];
        setSelectedTemplateId(defaultTemplate._id);
      } else {
        setSelectedTemplateId('');
      }
    }
  }, [selectedMainCategoryId, templates]);
  
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axiosInstance.get('/api/templates');
      console.log('Templates loaded in EnhancedUploadModal:', response.data);
      
      // Check if templates have fields
      const templatesWithFields = response.data.filter((t: ITemplate) => t.fields && t.fields.length > 0);
      console.log('Templates with fields:', templatesWithFields.length);
      
      if (templatesWithFields.length > 0) {
        console.log('First template fields:', templatesWithFields[0].fields);
      }
      
      setTemplates(response.data);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(`Failed to load templates: ${err.message}`);
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const resetState = () => {
    setActiveStep(0);
    setFiles([]);
    setDragActive(false);
    setLoading(false);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    setUploadStats(createInitialUploadStats());
    setSelectedTemplateId('');
    setSelectedTemplate(null);
    setSelectedMainCategoryId('');
    setCommonMetadata({});
    setCurrentFileIndex(0);
    setApplyToAllFiles(true);
    setSelectedSubcategoryIds([]);
  };
  
  // Handle file selection
  const handleFileSelection = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const { acceptedFiles, rejectedFiles } = splitFilesByUploadSize(selectedFiles);

    if (rejectedFiles.length > 0) {
      setError(buildUploadTooLargeMessage(rejectedFiles));
    } else {
      setError(null);
    }

    if (acceptedFiles.length === 0) return;
    
    // Convert FileList to IUploadFile array
    const newFiles: IUploadFile[] = acceptedFiles.map(file => {
      // Create image preview URL
      const preview = file.type.startsWith('image/') 
        ? URL.createObjectURL(file)
        : ''; // Could use generic icon for non-images
        
      return {
        id: uuidv4(),
        file,
        preview,
        metadata: {
          subcategoryIds: [] // Initialize subcategoryIds as empty array
        },
        uploadProgress: 0,
        status: 'pending'
      };
    });
    
    // Add to existing files
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
  };
  
  // Auto-fill per-file Artikelnummer from filename when main category is Products
  useEffect(() => {
    if (!files || files.length === 0) return;

    const main = categories.find(cat => cat._id === selectedMainCategoryId);
    const isProducts = !!main && (main.name || '').toLowerCase().includes('produkt');
    if (!isProducts) return;

    const artField = getArticleNumberField();
    if (!artField) return;

    let changed = false;
    const nextFiles = files.map(file => {
      const currentIdVal = file?.metadata?.[artField.id];
      const currentNameVal = artField.name ? file?.metadata?.[artField.name] : undefined;
      // Do not override if already set (manual input or earlier pass)
      const hasExisting =
        (currentIdVal !== undefined && String(currentIdVal).trim() !== '') ||
        (currentNameVal !== undefined && String(currentNameVal).trim() !== '');
      if (hasExisting) return file;

      const extracted = extractArticleNumber(file.file?.name || '');
      if (!extracted) return file;

      const newMetadata = { ...(file.metadata || {}) };
      newMetadata[artField.id] = extracted;
      if (artField.name && artField.name !== artField.id) {
        newMetadata[artField.name] = extracted;
      }
      changed = true;
      return { ...file, metadata: newMetadata };
    });

    if (changed) {
      setFiles(nextFiles);
    }
  // Intentionally depend on these to react when user selects category, template, or adds files
  }, [files, selectedMainCategoryId, selectedTemplate, categories]);

  // VIN: keep title in sync (Produktnamn_vin_id wins; otherwise default to filename w/o extension)
  useEffect(() => {
    if (!files || files.length === 0) return;

    const main = categories.find(cat => cat._id === selectedMainCategoryId);
    const isWine = !!main && (main.name || '').toLowerCase().includes('vin');
    if (!isWine) return;

    const pnField: any = getWineProductNameField();

    const getBaseTitle = (name: string): string => {
      const base = (name || '').split(/(\\|\/)/).pop() || name || '';
      return base.replace(/\.[^./\\]+$/, '');
    };

    let changed = false;
    const nextFiles = files.map((file: any) => {
      const meta = file.metadata || {};
      const currentTitle = String(meta.title || '').trim();

      const pnRaw = pnField
        ? (meta[pnField.id] ?? meta[pnField.name] ?? meta['Produktnamn_vin_id'])
        : (meta['Produktnamn_vin_id']);
      const productName = String(pnRaw || '').trim();

      // If Produktnamn is set, it must win → sync title
      if (productName) {
        if (currentTitle !== productName) {
          changed = true;
          return { ...file, metadata: { ...meta, title: productName } };
        }
        return file;
      }

      // If title is empty, default to filename (without extension)
      if (!currentTitle) {
        const baseTitle = getBaseTitle(file.file?.name || '');
        if (baseTitle) {
          changed = true;
          return { ...file, metadata: { ...meta, title: baseTitle } };
        }
      }

      return file;
    });

    if (changed) {
      setFiles(nextFiles);
    }
  }, [files, selectedMainCategoryId, selectedTemplate, categories]);

  // VIN (Common metadata mode): keep the visible "Titel" field synced to Produktnamn_vin_id when set
  useEffect(() => {
    const main = categories.find(cat => cat._id === selectedMainCategoryId);
    const isWine = !!main && (main.name || '').toLowerCase().includes('vin');
    if (!isWine) return;

    const pnField: any = getWineProductNameField();
    const pnRaw = pnField
      ? ((commonMetadata as any)[pnField.id] ?? (commonMetadata as any)[pnField.name] ?? (commonMetadata as any)['Produktnamn_vin_id'])
      : (commonMetadata as any)['Produktnamn_vin_id'];
    const productName = String(pnRaw || '').trim();
    if (!productName) return;

    const currentTitle = String((commonMetadata as any).title || '').trim();
    if (currentTitle === productName) return;

    setCommonMetadata(prev => ({ ...prev, title: productName }));
  }, [commonMetadata, selectedMainCategoryId, selectedTemplate, categories]);

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);
  
  // Handle drop event
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelection(e.dataTransfer.files);
    }
  }, []);
  
  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelection(e.target.files);
  };
  
  // Handle removing a file
  const handleRemoveFile = (fileId: string) => {
    setFiles(prevFiles => {
      const newFiles = prevFiles.filter(file => file.id !== fileId);
      
      // If removing the current file, adjust currentFileIndex
      if (currentFileIndex >= newFiles.length && newFiles.length > 0) {
        setCurrentFileIndex(newFiles.length - 1);
      }
      
      return newFiles;
    });
  };
  
  // Handle next step
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  // Handle template selection
  const handleTemplateChange = (e: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }) => {
    setSelectedTemplateId(e.target.value);
  };
  
  // Handle main category selection
  const handleMainCategoryChange = (e: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }) => {
    setSelectedMainCategoryId(e.target.value);
  };
  
  // Handle common metadata changes
  const handleCommonMetadataChange = (fieldId: string, value: any) => {
    setCommonMetadata(prev => ({ ...prev, [fieldId]: value }));
    
    // Apply to all files if checkbox is checked
    if (applyToAllFiles) {
      setFiles(prevFiles => prevFiles.map(file => ({
        ...file,
        metadata: { ...file.metadata, [fieldId]: value }
      })));
    }
  };
  
  // Handle per-file metadata changes
  const handleFileMetadataChange = (fileId: string, fieldId: string, value: any) => {
    setFiles(prevFiles => prevFiles.map(file => {
      if (file.id === fileId) {
        return {
          ...file,
          metadata: { ...file.metadata, [fieldId]: value }
        };
      }
      return file;
    }));
  };

  const templateUrlValidationErrors = useMemo(() => {
    if (!selectedTemplate) {
      return [];
    }

    if (applyToAllFiles) {
      return getTemplateUrlValidationErrors(selectedTemplate, commonMetadata, {
        showUploadFields: true,
        showEditFields: false,
      });
    }

    return files.flatMap((file) =>
      getTemplateUrlValidationErrors(selectedTemplate, file.metadata || {}, {
        showUploadFields: true,
        showEditFields: false,
      }),
    );
  }, [applyToAllFiles, commonMetadata, files, selectedTemplate]);

  const hasInvalidTemplateUrls = templateUrlValidationErrors.length > 0;
  
  // Handle changing the current file
  const handleNextFile = () => {
    if (currentFileIndex < files.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
    }
  };
  
  const handlePreviousFile = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
    }
  };
  
  // Handle subcategory selection
  const handleSubcategoryChange = (event: any) => {
    const selectedIds = event.target.value;
    setSelectedSubcategoryIds(selectedIds);
    
    // Apply to all files if in Common Metadata mode
    if (applyToAllFiles) {
      setFiles(prevFiles => prevFiles.map(file => ({
        ...file,
        metadata: { ...file.metadata, subcategoryIds: selectedIds }
      })));
    }
  };
  
  // Add a new function to handle deleting a subcategory
  const handleDeleteSubcategory = (categoryIdToDelete: string) => {
    const newSelectedIds = selectedSubcategoryIds.filter(id => id !== categoryIdToDelete);
    setSelectedSubcategoryIds(newSelectedIds);
    
    // Apply to all files if in Common Metadata mode
    if (applyToAllFiles) {
      setFiles(prevFiles => prevFiles.map(file => ({
        ...file,
        metadata: { ...file.metadata, subcategoryIds: newSelectedIds }
      })));
    }
  };
  
  // Add a new function to handle deleting a subcategory for a specific file
  const handleDeleteFileSubcategory = (fileId: string, categoryIdToDelete: string) => {
    setFiles(prevFiles => prevFiles.map(file => {
      if (file.id === fileId) {
        const newSubcategoryIds = (file.metadata.subcategoryIds || []).filter((id: string) => id !== categoryIdToDelete);
        return {
          ...file,
          metadata: { ...file.metadata, subcategoryIds: newSubcategoryIds }
        };
      }
      return file;
    }));
  };
  
  // Upload the files
  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Inga filer valda');
      return;
    }
    
    if (!selectedTemplate) {
      setError('Ingen mall vald');
      return;
    }

    if (hasInvalidTemplateUrls) {
      setError('Korrigera ogiltiga URL-falt innan du laddar upp.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    const totalBytes = files.reduce((sum, fileItem) => sum + (fileItem.file?.size || 0), 0);
    const uploadStartTime = Date.now();
    let completedBytes = 0;
    setUploadStats({
      uploadedBytes: 0,
      totalBytes,
      speedBps: 0,
      etaSeconds: undefined,
      currentFileName: files[0]?.file?.name,
      currentFileIndex: 0,
      currentFileProgress: 0,
      phase: 'uploading',
    });
    
    try {
      let successCount = 0;
      const uploadedAssetIds = new Set<string>();
      const uploadedCategoryIds = new Set<string>();
      
      // Update file status to uploading
      setFiles(prevFiles => prevFiles.map(file => ({
        ...file,
        status: 'uploading',
        uploadProgress: 0
      })));
      
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadStats(prev => ({
          ...prev,
          currentFileName: file.file?.name,
          currentFileIndex: i,
          currentFileProgress: 0,
          phase: 'uploading',
          speedBps: 0,
          etaSeconds: undefined,
        }));
        const formData = new FormData();
        
        // Add file
        formData.append('asset', file.file);
        if (destination === 'archive') formData.append('archived', 'true');
        // Idempotency key for this file upload (prevents duplicates on retry/refresh)
        if (file.id) formData.append('clientUploadId', file.id);
        if (selectedTemplate?._id) formData.append('templateId', selectedTemplate._id);
        
        // Add metadata from template
        if (selectedTemplate) {
          // Get metadata for this file
          const metadata = file.metadata;
          
          // Add basic metadata fields
          if (metadata.title) formData.append('title', metadata.title);
          if (metadata.description) formData.append('description', metadata.description);
          if (metadata.tags) {
            // Handle tags as array or comma-separated string
            const tags = Array.isArray(metadata.tags) 
              ? metadata.tags.join(',') 
              : metadata.tags;
            formData.append('tags', tags);
          }
          
          // Add main category if selected
          if (selectedMainCategoryId) {
            formData.append('categories', selectedMainCategoryId);
          }
          
          // Add subcategories if selected (common or per-file)
          const fileSubcategories = applyToAllFiles 
            ? selectedSubcategoryIds 
            : (metadata.subcategoryIds || []);
            
          if (fileSubcategories && fileSubcategories.length > 0) {
            // Append each subcategory
            fileSubcategories.forEach((categoryId: string) => {
              formData.append('categories', categoryId);
            });
          }
          
          // Add top-level Validoo skip flag if present
          if (Object.prototype.hasOwnProperty.call(metadata, 'skipValidooSync')) {
            formData.append('skipValidooSync', String(!!(metadata as any).skipValidooSync));
          }

          const normalizeCheckboxValue = (val: any) => {
            if (val === true || val === false) return val;
            if (typeof val === 'string') {
              const normalized = val.trim().toLowerCase();
              if (['true', '1', 'yes'].includes(normalized)) return true;
              if (['false', '0', 'no'].includes(normalized)) return false;
            }
            return undefined;
          };

          const appendedMetadataKeys = new Set<string>();
          const appendMetadataField = (metadataField: string, value: any) => {
            if (appendedMetadataKeys.has(metadataField)) return;
            appendedMetadataKeys.add(metadataField);
            if (typeof value === 'object' && !(value instanceof Date)) {
              formData.append(metadataField, JSON.stringify(value));
            } else if (value instanceof Date) {
              formData.append(metadataField, value.toISOString());
            } else {
              formData.append(metadataField, String(value));
            }
          };

          // Add custom metadata fields based on template definition
          selectedTemplate.fields.forEach(field => {
            if (field.type === 'separator') return;

            const idValue = metadata[field.id];
            const nameValue = field.name ? metadata[field.name] : undefined;
            let resolvedValue = idValue;

            if (resolvedValue === undefined || resolvedValue === '') {
              resolvedValue = nameValue;
            }

            if (field.type === 'checkbox') {
              const normalizedId = normalizeCheckboxValue(idValue);
              const normalizedName = normalizeCheckboxValue(nameValue);

              if (normalizedId === false && normalizedName === true) {
                resolvedValue = true;
              } else if (normalizedId !== undefined) {
                resolvedValue = normalizedId;
              } else if (normalizedName !== undefined) {
                resolvedValue = normalizedName;
              }
            }

            if (
              resolvedValue === null
              || resolvedValue === undefined
              || resolvedValue === ''
              || (Array.isArray(resolvedValue) && resolvedValue.length === 0)
            ) return;

            appendMetadataField(`metadata.${field.id}`, resolvedValue);
            if (field.name && field.name !== field.id) {
              appendMetadataField(`metadata.${field.name}`, resolvedValue);
            }
          });
        }
        
        // Upload the file
        try {
          const response = await axiosInstance.post('/api/assets', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const uploadedNow = completedBytes + progressEvent.loaded;
                const overallProgress = totalBytes > 0 ? Math.round((uploadedNow * 100) / totalBytes) : 0;
                const elapsedMs = Math.max(1, Date.now() - uploadStartTime);
                const speedBps = (uploadedNow * 1000) / elapsedMs;
                const remainingBytes = Math.max(0, totalBytes - uploadedNow);
                const etaSeconds = speedBps > 0 ? Math.round(remainingBytes / speedBps) : undefined;
                const phase: UploadPhase = progressEvent.loaded >= progressEvent.total ? 'processing' : 'uploading';
                
                // Update progress for this file
                setFiles(prevFiles => prevFiles.map(f => {
                  if (f.id === file.id) {
                    return { ...f, uploadProgress: progress };
                  }
                  return f;
                }));
                
                // Update overall progress
                setUploadProgress(overallProgress);
                setUploadStats({
                  uploadedBytes: uploadedNow,
                  totalBytes,
                  speedBps,
                  etaSeconds,
                  currentFileName: file.file?.name,
                  currentFileIndex: i,
                  currentFileProgress: progress,
                  phase,
                });
              }
            }
          });

          const uploadedAssets = (Array.isArray(response?.data) ? response.data : [response?.data]) as Array<{
            _id?: string;
            categories?: string[];
          }>;
          uploadedAssets.forEach((asset) => {
            if (asset?._id) {
              uploadedAssetIds.add(String(asset._id));
            }
            if (Array.isArray(asset?.categories)) {
              asset.categories.forEach((categoryId) => {
                if (categoryId) {
                  uploadedCategoryIds.add(String(categoryId));
                }
              });
            }
          });
          
          // Mark file as uploaded
          setFiles(prevFiles => prevFiles.map(f => {
            if (f.id === file.id) {
              return { ...f, status: 'uploaded', uploadProgress: 100 };
            }
            return f;
          }));
          
          successCount++;
          completedBytes += file.file?.size || 0;
          setUploadStats(prev => ({
            ...prev,
            uploadedBytes: completedBytes,
            totalBytes,
            speedBps: 0,
            etaSeconds: i === files.length - 1 ? 0 : undefined,
            currentFileName: file.file?.name,
            currentFileIndex: i,
            currentFileProgress: 100,
            phase: i === files.length - 1 ? 'completed' : 'uploading',
          }));
          // Trigger immediate grid refresh per file (App listens to this event)
          try {
            window.dispatchEvent(new CustomEvent('dam:asset-uploaded'));
          } catch {}
        } catch (err: any) {
          console.error(`Error uploading file ${file.file.name}:`, err);
          setUploadStats(prev => ({
            ...prev,
            currentFileName: file.file?.name,
            currentFileIndex: i,
            currentFileProgress: 0,
            phase: 'error',
            speedBps: 0,
            etaSeconds: undefined,
          }));
          
          // Mark file as failed
          setFiles(prevFiles => prevFiles.map(f => {
            if (f.id === file.id) {
              return { 
                ...f, 
                status: 'failed', 
                error: getUploadErrorMessage(err) 
              };
            }
            return f;
          }));
        }
      }
      
      // Set success if at least one file was uploaded
      if (successCount > 0) {
        setSuccess(true);
        setUploadStats(prev => ({
          ...prev,
          uploadedBytes: totalBytes,
          totalBytes,
          speedBps: 0,
          etaSeconds: 0,
          currentFileProgress: 100,
          phase: 'completed',
        }));

        onUploadSuccess?.();

        window.setTimeout(() => {
          try {
            window.dispatchEvent(new CustomEvent('dam:upload-batch-completed', {
              detail: {
                assetIds: Array.from(uploadedAssetIds),
                categoryIds: Array.from(uploadedCategoryIds),
              },
            }));
          } catch {}
        }, 100);
      } else {
        setError('Alla uppladdningar misslyckades');
        setUploadStats(prev => ({
          ...prev,
          speedBps: 0,
          etaSeconds: undefined,
          phase: 'error',
        }));
      }
    } catch (err: any) {
      setError(`Uppladdningen misslyckades: ${getUploadErrorMessage(err)}`);
      setUploadStats(prev => ({
        ...prev,
        speedBps: 0,
        etaSeconds: undefined,
        phase: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  // Run upload in background via UploadManager and close modal
  const handleRunInBackground = () => {
    if (files.length === 0 || !selectedTemplate) return;
    enqueueBackgroundUpload({
      title: 'Bulk-uppladdning',
      files: files.map((f: any) => ({ id: f.id, file: f.file, metadata: f.metadata || {} })),
      selectedTemplate,
      selectedMainCategoryId,
      selectedSubcategoryIds,
      applyToAllFiles
    });
    if (onClose) onClose();
  };
  
  // Render the current step
  const renderStep = () => {
    switch (activeStep) {
      case 0: // Select Files
        return (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: dragActive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                transition: 'all 0.2s',
                cursor: 'pointer',
                minHeight: '200px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />
              <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Dra och släpp filer här
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                eller klicka för att välja
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }}>
                Välj filer
              </Button>
            </Box>
            
            {files.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Valda filer ({files.length})
                </Typography>
                <Grid container spacing={2}>
                  {files.map((file) => (
                    <Grid key={file.id} sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Card variant="outlined" sx={{ width: 220, mx: 'auto' }}>
                        <CardContent sx={{ p: 1, pb: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: '80%' }}>
                              {file.file.name}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveFile(file.id)}
                              aria-label="Ta bort fil"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                        
                        {file.preview && (
                          <CardMedia
                            component="img"
                            height="140"
                            image={file.preview}
                            alt={file.file.name}
                            sx={{ objectFit: 'contain', p: 1 }}
                          />
                        )}
                        
                        <CardContent sx={{ pt: 0 }}>
                          <Typography variant="caption" color="textSecondary">
                            {formatUploadBytes(file.file.size)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        );
        
      case 1: // Choose Template
        return (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Välj kategori
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(4, auto)',
                justifyContent: 'center',
                gap: 2,
                mb: 1
              }}>
                {categories
                  .filter(category => !category.parentCategory)
                  .map((category) => {
                    const IconComponent = getCategoryIcon(category.name);
                    const isSelected = selectedMainCategoryId === category._id;
                    
                    return (
                      <Tooltip key={category._id} title={category.name} placement="top">
                        <Button
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => setSelectedMainCategoryId(category._id)}
                          sx={{
                            minWidth: 150,
                            minHeight: 150,
                            width: 150,
                            height: 150,
                            borderRadius: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            fontWeight: isSelected ? 600 : 500,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: isSelected 
                              ? '0 8px 24px rgba(0, 0, 0, 0.15)' 
                              : '0 4px 12px rgba(0, 0, 0, 0.08)',
                            '&:hover': {
                              transform: 'translateY(-4px) scale(1.02)',
                              boxShadow: isSelected 
                                ? '0 12px 32px rgba(0, 0, 0, 0.2)' 
                                : '0 8px 24px rgba(0, 0, 0, 0.12)',
                            },
                            '& .MuiButton-startIcon': {
                              margin: 0
                            }
                          }}
                        >
                          <IconComponent 
                            sx={{ 
                              fontSize: 40,
                              color: isSelected ? 'primary.contrastText' : 'primary.main'
                            }} 
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              lineHeight: 1.2,
                              textAlign: 'center',
                              color: isSelected ? 'primary.contrastText' : 'text.primary'
                            }}
                          >
                            {category.name}
                          </Typography>
                        </Button>
                      </Tooltip>
                    );
                  })}
              </Box>
              {selectedMainCategoryId && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.100' }}>
                  <Typography variant="body2" color="primary.main">
                    Vald kategori: {categories.find(cat => cat._id === selectedMainCategoryId)?.name}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {loadingTemplates ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : templates.length === 0 ? (
              <Alert severity="info">
                Inga mallar tillgängliga. Skapa en mall först.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="template-label">Mall</InputLabel>
                  <Select
                    labelId="template-label"
                    value={selectedTemplateId}
                    onChange={handleTemplateChange}
                    label="Mall"
                    disabled={!selectedMainCategoryId}
                  >
                    <MenuItem value="">
                      <em>Välj mall</em>
                    </MenuItem>
                    {templates
                      .filter(template => 
                        !selectedMainCategoryId || 
                        template.categoryIds.includes(selectedMainCategoryId)
                      )
                      .map((template) => (
                        <MenuItem key={template._id} value={template._id}>
                          {template.name} {template.isDefault && '(Standard)'}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
                
                {selectedTemplate && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Mall: {selectedTemplate.name}
                    </Typography>
                    {selectedTemplate.description && (
                      <Typography variant="body2" gutterBottom>
                        {selectedTemplate.description}
                      </Typography>
                    )}
                    <Typography variant="body2" color="textSecondary">
                      {selectedTemplate.fields.length} tillgängliga fält
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        );
        
      case 2: // Add Metadata
        return (
          <Box sx={{ p: 2 }}>
            {!selectedTemplate ? (
              <Alert severity="warning">
                Välj mall först
              </Alert>
            ) : (
              <>
                <Tabs 
                  value={applyToAllFiles ? 0 : 1} 
                  onChange={(_, value) => setApplyToAllFiles(value === 0)}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Gemensam metadata" />
                  <Tab label="Per‑fil metadata" />
                </Tabs>

                {hasInvalidTemplateUrls && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Korrigera ogiltiga URL-falt innan du fortsatter.
                  </Alert>
                )}
                
                {applyToAllFiles ? (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Gemensam metadata (för alla filer)
                    </Typography>
                    
                    {/* Category selection */}
                    {selectedMainCategoryId && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Välj underkategorier
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel id="subcategories-label">Underkategorier</InputLabel>
                          <Select
                            labelId="subcategories-label"
                            multiple
                            value={selectedSubcategoryIds}
                            onChange={handleSubcategoryChange}
                            label="Underkategorier"
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((value) => {
                                  const category = categories.find(cat => cat._id === value);
                                  return (
                                    <Chip 
                                      key={value} 
                                      label={category ? category.name : value} 
                                      onDelete={() => handleDeleteSubcategory(value)}
                                      deleteIcon={
                                        <CloseIcon
                                          style={{ fontSize: '16px' }}
                                          onMouseDown={(event) => event.stopPropagation()}
                                        />
                                      }
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {categories
                              .filter(isChildOfSelectedCategory)
                              .map((category) => (
                                <MenuItem key={category._id} value={category._id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        {/* Validoo skip checkbox for Products main category */}
                        {(() => {
                          const main = categories.find(cat => cat._id === selectedMainCategoryId);
                          const isProducts = !!main && (main.name || '').toLowerCase().includes('produkt');
                          return isProducts ? (
                            <FormControlLabel
                              sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                              control={
                                <Checkbox
                                  checked={!!commonMetadata.skipValidooSync}
                                  onChange={(e) => handleCommonMetadataChange('skipValidooSync', e.target.checked)}
                                  size="small"
                                />
                              }
                              label="Synka inte med Validoo"
                            />
                          ) : null;
                        })()}
                      </Box>
                    )}
                    
                    {/* Standard fields - always show these */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Standardmetadata
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          label="Titel"
                          value={commonMetadata.title || ''}
                          onChange={(e) => handleCommonMetadataChange('title', e.target.value)}
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Beskrivning"
                          value={commonMetadata.description || ''}
                          onChange={(e) => handleCommonMetadataChange('description', e.target.value)}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <TagsChipsInput
                          value={commonMetadata.tags || ''}
                          onChange={(val) => handleCommonMetadataChange('tags', val)}
                          label="Taggar (kommaseparerade)"
                          helperText="Tryck Enter för att lägga till tagg"
                        />
                      </Box>
                      
                      {/* AI generated content display */}
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          Förhandsvisning av AI‑genererat innehåll
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Beskrivning:</strong> {commonMetadata.aiDescription || 'Skapas under uppladdning'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Taggar:</strong> {commonMetadata.aiTags || 'Skapas under uppladdning'}
                        </Typography>
                      </Paper>
                    </Box>
                    
                    {/* Template custom fields */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Mallfält
                      </Typography>
                      <TemplateFieldsForm
                        template={selectedTemplate}
                        values={commonMetadata}
                        onChange={handleCommonMetadataChange}
                        showUploadFields={true}
                        showEditFields={false}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Per‑fil metadata
                    </Typography>
                    
                    {/* Files list with metadata editors - vertical scrolling layout */}
                    {files.map((file, index) => (
                      <Paper 
                        key={file.id} 
                        elevation={1} 
                        sx={{ 
                          p: 2, 
                          mb: 3, 
                          border: currentFileIndex === index ? '2px solid' : '1px solid',
                          borderColor: currentFileIndex === index ? 'primary.main' : 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                          {/* File preview */}
                          <Box sx={{ width: { xs: '100%', sm: '200px' } }}>
                            {file.preview ? (
                              <img 
                                src={file.preview} 
                                alt={file.file.name}
                                style={{ 
                                  width: '100%',
                                  maxHeight: '150px',
                                  objectFit: 'contain'
                                }}
                              />
                            ) : (
                              <Box sx={{ height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                                <Typography variant="body2" color="textSecondary">Ingen förhandsvisning</Typography>
                              </Box>
                            )}
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {file.file.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {formatUploadBytes(file.file.size)}
                            </Typography>
                            
                            {/* Subcategory selection for this file */}
                            {selectedMainCategoryId && (
                              <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel id={`file-${index}-subcategories-label`} size="small">Underkategorier</InputLabel>
                                <Select
                                  labelId={`file-${index}-subcategories-label`}
                                  multiple
                                  value={file.metadata.subcategoryIds || []}
                                  onChange={(e) => handleFileMetadataChange(file.id, 'subcategoryIds', e.target.value)}
                                  label="Underkategorier"
                                  size="small"
                                  renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {(selected as string[]).map((value) => {
                                        const category = categories.find(cat => cat._id === value);
                                        return (
                                          <Chip 
                                            key={value} 
                                            label={category ? category.name : value} 
                                            size="small"
                                            onDelete={() => handleDeleteFileSubcategory(file.id, value)}
                                            deleteIcon={
                                              <CloseIcon
                                                style={{ fontSize: '14px' }}
                                                onMouseDown={(event) => event.stopPropagation()}
                                              />
                                            }
                                          />
                                        );
                                      })}
                                    </Box>
                                  )}
                                >
                                  {categories
                                    .filter(isChildOfSelectedCategory)
                                    .map((category) => (
                                      <MenuItem key={category._id} value={category._id}>
                                        {category.name}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            )}
                            {/* Per-file Validoo skip checkbox for Products main category */}
                            {(() => {
                              const main = categories.find(cat => cat._id === selectedMainCategoryId);
                              const isProducts = !!main && (main.name || '').toLowerCase().includes('produkt');
                              return isProducts ? (
                                <FormControlLabel
                                  sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                                  control={
                                    <Checkbox
                                      checked={!!file.metadata.skipValidooSync}
                                      onChange={(e) => handleFileMetadataChange(file.id, 'skipValidooSync', e.target.checked)}
                                      size="small"
                                    />
                                  }
                                  label="Synka inte med Validoo"
                                />
                              ) : null;
                            })()}
                          </Box>
                          
                          {/* Metadata fields */}
                          <Box sx={{ flex: 1 }}>
                            {/* Standard fields */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>Standardmetadata</Typography>
                              <Box sx={{ mb: 2 }}>
                                <TextField
                                  fullWidth
                                  label="Titel"
                                  size="small"
                                  value={file.metadata.title || ''}
                                  onChange={(e) => handleFileMetadataChange(file.id, 'title', e.target.value)}
                                />
                              </Box>
                              <Box sx={{ mb: 2 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  label="Beskrivning"
                                  size="small"
                                  value={file.metadata.description || ''}
                                  onChange={(e) => handleFileMetadataChange(file.id, 'description', e.target.value)}
                                />
                              </Box>
                              <Box sx={{ mb: 2 }}>
                                <TagsChipsInput
                                  value={file.metadata.tags || ''}
                                  onChange={(val) => handleFileMetadataChange(file.id, 'tags', val)}
                                  size="small"
                                  label="Taggar (kommaseparerade)"
                                  helperText="Tryck Enter för att lägga till tagg"
                                />
                              </Box>
                            </Box>
                            
                            {/* Template fields */}
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Mallfält</Typography>
                              <TemplateFieldsForm
                                template={selectedTemplate}
                                values={file.metadata}
                                onChange={(fieldId, value) => handleFileMetadataChange(file.id, fieldId, value)}
                                showUploadFields={true}
                                showEditFields={false}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        );
        
      case 3: // Upload
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Uppladdningsstatus
            </Typography>
            
            <Box sx={{ width: '100%', mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  color={
                    uploadStats.phase === 'completed'
                      ? 'success'
                      : uploadStats.phase === 'error'
                        ? 'error'
                        : uploadStats.phase === 'processing'
                          ? 'info'
                          : 'primary'
                  }
                  label={
                    uploadStats.phase === 'completed'
                      ? 'Klar'
                      : uploadStats.phase === 'error'
                        ? 'Misslyckades'
                        : uploadStats.phase === 'processing'
                          ? 'Bearbetar på servern'
                          : 'Laddar upp'
                  }
                />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {formatUploadBytes(uploadStats.uploadedBytes)} / {formatUploadBytes(uploadStats.totalBytes)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ height: 12, borderRadius: 6 }}
              />
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  {uploadProgress} % klart
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {uploadStats.currentFileName
                    ? `Fil ${Math.min(uploadStats.currentFileIndex + 1, files.length)} av ${files.length}: ${uploadStats.currentFileName}`
                    : 'Väntar på uppladdning'}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {uploadStats.phase === 'processing'
                    ? 'Servern bearbetar filen efter uppladdning.'
                    : `Aktuell fil: ${Math.round(uploadStats.currentFileProgress)}%`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {uploadStats.phase === 'processing'
                    ? 'Fart: - • ETA: -'
                    : `Fart: ${formatUploadSpeed(uploadStats.speedBps)} • ETA: ${formatUploadEta(uploadStats.etaSeconds)}`}
                </Typography>
              </Box>
            </Box>
            
            <Box>
              {files.map((file) => (
                <Card key={file.id} variant="outlined" sx={{ mb: 1, px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 360 }}>
                      {file.file.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {file.status === 'pending' && (
                      <Chip size="small" label="Väntar" />
                    )}
                    {file.status === 'uploading' && (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {uploadStats.phase === 'processing' && uploadStats.currentFileName === file.file.name
                            ? 'Bearbetar...'
                            : `${file.uploadProgress}%`}
                        </Typography>
                      </>
                    )}
                    {file.status === 'uploaded' && (
                      <Chip size="small" label="Uppladdad" color="success" icon={<CheckCircleIcon />} />
                    )}
                    {file.status === 'failed' && (
                      <Chip size="small" label={file.error || 'Misslyckades'} color="error" icon={<ErrorIcon />} />
                    )}
                  </Box>
                </Card>
              ))}
            </Box>
            
            {success && (
              <Alert severity="success" sx={{ mt: 3 }}>
                Uppladdningen är klar!
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}
          </Box>
        );
        
      default:
        return null;
    }
  };
  
  // Determine if next button should be disabled
  const isNextDisabled = () => {
    switch (activeStep) {
      case 0:
        return files.length === 0;
      case 1:
        return !selectedTemplate;
      case 2:
        return hasInvalidTemplateUrls;
      default:
        return false;
    }
  };
  
  // Better handling of parentCategory in different formats
  const isChildOfSelectedCategory = (category: ICategory) => {
    if (!category.parentCategory || !selectedMainCategoryId) {
      return false;
    }
    
    // If parentCategory is a string
    if (typeof category.parentCategory === 'string') {
      return category.parentCategory === selectedMainCategoryId;
    }
    
    // If parentCategory is an object with _id as string
    if (typeof category.parentCategory === 'object' && category.parentCategory !== null) {
      if ('_id' in category.parentCategory) {
        const parentId = (category.parentCategory as {_id: string})._id;
        return parentId === selectedMainCategoryId;
      }
    }
    
    return false;
  };
  
  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="enhanced-upload-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '800px',
        maxWidth: '800px',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 1,
        height: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box>
            <Typography id="enhanced-upload-modal-title" variant="h5" component="h2">
              Ladda upp filer
            </Typography>
            {destination === 'archive' && (
              <Typography variant="body2" color="text.secondary">
                Filen läggs i arkivet och syns inte i biblioteket.
              </Typography>
            )}
          </Box>
          
          {!loading && (
            <IconButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        
        <Stepper activeStep={activeStep} sx={{ px: 3, py: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {renderStep()}
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Tillbaka
          </Button>
          
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={files.length === 0 || loading || success}
                startIcon={loading ? <CircularProgress size={20} /> : (success ? <DoneAllIcon /> : undefined)}
              >
                {loading ? 'Laddar upp...' : (success ? 'Uppladdad' : 'Ladda upp')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={isNextDisabled()}
              >
                Nästa
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button sx={{ ml: 2 }} onClick={handleRunInBackground} disabled={files.length === 0 || success}>
                Kör i bakgrunden
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EnhancedUploadModal; 